import {
    differenceInMonths,
    differenceInWeeks,
    endOfMonth,
    endOfWeek,
    format,
    isBefore,
    isThisMonth,
    isThisWeek,
    parse,
    startOfMonth,
    startOfWeek,
} from 'date-fns';

import { RequestPreviewRanges } from '@/domain/newRequest';
import { DATE_FORMAT } from '@/utils/date/date';
import { PERIOD } from '@/utils/enums/common';

export const handleRangesInfo = (periodType: PERIOD | undefined, ranges: RequestPreviewRanges[]) => {
    if (periodType) {
        const SINGLE_TIME_PERIOD = 1;
        const MULTIPLE_TIME_PERIODS = 2;
        const formatRegex = /-/g;
        const multipleRanges = ranges.length > 1;
        const currentDate = new Date();
        const lastPeriodStartDate = ranges[ranges.length - 1].startDate.replace(formatRegex, '.');
        const lastPeriodEndDate = ranges[ranges.length - 1].endDate.replace(formatRegex, '.');

        const parseMinDate = parse(ranges[0].startDate, DATE_FORMAT.DAY_MONTH_YEAR, new Date());
        const parseMaxDate = parse(ranges[ranges.length - 1].endDate, DATE_FORMAT.DAY_MONTH_YEAR, new Date());
        if (periodType === PERIOD.MONTH) {
            const startOfCurrentMonth = startOfMonth(currentDate);
            const endOfCurrentMonth = endOfMonth(currentDate);

            const startOfCurrentRange = format(startOfCurrentMonth, DATE_FORMAT.FULL_WITH_DOTS);
            const endOfCurrentRange = format(endOfCurrentMonth, DATE_FORMAT.FULL_WITH_DOTS);

            const isCurrentMonth = isThisMonth(parseMinDate);
            const isPastMonth = isBefore(parseMinDate, startOfCurrentMonth);
            const isNextMonth = differenceInMonths(parseMinDate, startOfCurrentMonth) === SINGLE_TIME_PERIOD;
            const isAtLeastTwoMonthsAfter = differenceInMonths(parseMaxDate, currentDate) >= MULTIPLE_TIME_PERIODS;

            const commonData = {
                currentPeriodStartDate: startOfCurrentRange,
                currentPeriodEndDate: endOfCurrentRange,
                lastPeriodStartDate,
                lastPeriodEndDate,
            };

            const isRequestSpanningCurrentAndFutureMonths =
                multipleRanges && (isCurrentMonth || isPastMonth) && isAtLeastTwoMonthsAfter;
            const isRequestSpanningFutureMonths = (multipleRanges && isNextMonth) || isAtLeastTwoMonthsAfter;

            if (isRequestSpanningCurrentAndFutureMonths) {
                return {
                    isCurrentAndFuture: true,
                    ...commonData,
                };
            }

            if (isRequestSpanningFutureMonths) {
                return {
                    isCurrentAndFuture: false,
                    ...commonData,
                };
            }
        }

        if (periodType === PERIOD.WEEK) {
            const MONDAY = 1;
            const startOfCurrentWeek = startOfWeek(currentDate, { weekStartsOn: MONDAY });
            const endOfCurrentWeek = endOfWeek(currentDate, { weekStartsOn: MONDAY });

            const startOfCurrentRange = format(startOfCurrentWeek, DATE_FORMAT.FULL_WITH_DOTS);
            const endOfCurrentRange = format(endOfCurrentWeek, DATE_FORMAT.FULL_WITH_DOTS);

            const isCurrentWeek = isThisWeek(parseMinDate, { weekStartsOn: 1 });
            const isPastWeek = isBefore(parseMinDate, startOfCurrentWeek);
            const isNextWeek = differenceInWeeks(parseMinDate, startOfCurrentWeek) === SINGLE_TIME_PERIOD;
            const isAtLeastTwoWeeksAfter = differenceInWeeks(parseMaxDate, currentDate) >= MULTIPLE_TIME_PERIODS;

            const commonData = {
                currentPeriodStartDate: startOfCurrentRange,
                currentPeriodEndDate: endOfCurrentRange,
                lastPeriodStartDate,
                lastPeriodEndDate,
            };

            const isRequestSpanningCurrentAndFutureWeeks =
                multipleRanges && (isCurrentWeek || isPastWeek) && isAtLeastTwoWeeksAfter;

            const isRequestSpanningFutureWeeks = (multipleRanges && isNextWeek) || isAtLeastTwoWeeksAfter;

            if (isRequestSpanningCurrentAndFutureWeeks) {
                return {
                    isCurrentAndFuture: true,
                    ...commonData,
                };
            }

            if (isRequestSpanningFutureWeeks) {
                return {
                    isCurrentAndFuture: false,
                    ...commonData,
                };
            }
        }
    }

    return undefined;
};
